// src/features/map/labelCards.js
// "Stack as cards" (spec docs/superpowers/specs/2026-10-30-map-label-crowding-and-detail-design.md §3.3):
// each stacked vehicle label is baked into one icon image (no text pass), drawn by an
// icon-only symbol layer. This module is the pure side: a card's spec, its stable image
// id, and the registry the map's `styleimagemissing` handler renders from. Images are
// created lazily (only for cards MapLibre actually places) and evicted once unused.

/** Above this many vehicles (with a position) Stack falls back to Spread out. */
export const CARD_FLEET_CAP = 400;

// No opacity: cards are baked fully opaque so text on the card beneath never shows through.
const SPEC_KEYS = ['text', 'shape', 'bg', 'fg', 'textPx', 'tight'];
const SEL_PREFIX = 'card-sel-';
const BASE_PREFIX = 'card-';

/** The card's drawing spec from a feature's label props (`buildLabelProps`). */
export function cardSpec(props) {
  return {
    text: props.labelText,
    shape: props.labelShape,
    bg: props.labelBg,
    fg: props.labelFg,
    textPx: props.labelTextSize,
    tight: props.labelTight,
    selected: false,
  };
}

// FNV-1a 32-bit over the UTF-16 code units, as 8 hex chars.
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

const specKey = (spec) => JSON.stringify(SPEC_KEYS.map((k) => spec[k] ?? null));

/** `card-<hash>`, or `card-sel-<same hash>` for the selected variant. `selected`
 *  is not hashed, so the selected layer derives its image id by slicing `card-`. */
export function cardImageId(spec) {
  return (spec.selected ? SEL_PREFIX : BASE_PREFIX) + fnv1a(specKey(spec));
}

/** The base (unselected) id for any card id. */
export function baseCardId(id) {
  return id.startsWith(SEL_PREFIX) ? BASE_PREFIX + id.slice(SEL_PREFIX.length) : id;
}

const selectedTwinId = (baseId) => SEL_PREFIX + baseId.slice(BASE_PREFIX.length);

// Base (unselected) specs only, insertion-ordered so iteration is oldest first.
// The selected twin `card-sel-…` is derived from its base id, never registered.
const registry = new Map();

/** Register a card's spec and return its image id. Two different specs whose
 *  32-bit hashes collide get `-2`, `-3`, … so neither shows the other's text.
 *  `idOf` is injectable for tests. */
export function registerCardSpec(spec, { idOf = cardImageId } = {}) {
  const base = { ...spec, selected: false };
  const key = specKey(base);
  const first = idOf(base);
  let id = first;
  for (let n = 2; registry.has(id) && specKey(registry.get(id)) !== key; n += 1) id = `${first}-${n}`;
  if (!registry.has(id)) registry.set(id, base);
  return id;
}

export function getCardSpec(id) {
  return registry.get(id);
}

function removeImage(map, id) {
  if (map?.hasImage?.(id)) map.removeImage(id);
}

/** Every call: drop from the map each card image (and its selected twin) that no
 *  feature uses (`liveIds`). Specs stay registered so a late request can still
 *  re-render; the registry itself is trimmed of unused specs only past `keep`. */
export function evictUnusedCards(map, liveIds, { keep = 2000 } = {}) {
  for (const id of registry.keys()) {
    if (liveIds.has(id)) continue;
    removeImage(map, id);
    removeImage(map, selectedTwinId(id));
  }
  if (registry.size <= keep) return;
  for (const id of [...registry.keys()]) if (!liveIds.has(id)) registry.delete(id);
}

/** Drop every loaded card image (e.g. once the card font has loaded) so MapLibre
 *  re-requests and the cards re-render; the specs stay registered. */
export function removeAllCardImages(map) {
  for (const id of registry.keys()) {
    removeImage(map, id);
    removeImage(map, selectedTwinId(id));
  }
}

export function resetCardRegistry() {
  registry.clear();
}
